import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import type { TemplateFormPrefill } from "../utils/templateForm";

type Maybe = string | null | undefined;

/** Both the loaded building detail and the list summary carry these. */
interface BuildingFacts {
  address: Maybe;
  registry_code: Maybe;
}

function text(value: Maybe): string {
  if (!value) return "";
  return value;
}

function either(first: Maybe, second: Maybe): string {
  const value = text(first);
  if (value) return value;
  return text(second);
}

function authorName(first: Maybe, last: Maybe, email: Maybe): string {
  const name = `${text(first)} ${text(last)}`.trim();
  return either(name, email);
}

function addressOf(detail: BuildingFacts | null, summary: BuildingFacts | null): string {
  return either(text(detail && detail.address), text(summary && summary.address));
}

function registryCodeOf(detail: BuildingFacts | null, summary: BuildingFacts | null): string {
  return either(text(detail && detail.registry_code), text(summary && summary.registry_code));
}

/**
 * The values a template form opens with: today's date plus whatever Franklin
 * already knows about the building, the organisation and the current user.
 * Everything else starts empty — the form never invents a value.
 */
export function useTemplateFormPrefill(
  buildingId: string,
  buildingName: string
): TemplateFormPrefill {
  const { userProfile } = useAuth();
  const { currentOrganization } = useOrganizationContext();
  const buildingStore = useBuildingStore();

  const loaded = buildingStore.getById(buildingId);
  const detail: BuildingFacts | null = loaded ? loaded.base : null;
  const summary = buildingStore.getList().find((item) => item.id === buildingId) ?? null;
  const profile = userProfile;

  return {
    today: format(new Date(), "yyyy-MM-dd"),
    organisation: text(currentOrganization && currentOrganization.name),
    buildingName,
    buildingAddress: addressOf(detail, summary),
    registryCode: registryCodeOf(detail, summary),
    author: profile ? authorName(profile.first_name, profile.last_name, profile.email) : "",
  };
}
